/*
 * nadi-deal-value.js — make a quantity deal look like the deal it is.
 *
 * WHAT THE PILLS SAY TODAY. On a product with 1 / 6 / 12 packs at ₪99 / ₪288 /
 * ₪552, the pills read "6 יחידות · ₪288 · ₪48.00 ליחידה · חוסך 52%".
 *
 * WHAT THEY DO NOT SAY. That the customer keeps ₪306. Six units at the single
 * price is ₪594; the pack is ₪288. The percentage is the same fact expressed
 * in the form people are worst at feeling, and the shekel figure — the one a
 * barber can put against a month of stock — is nowhere on the page.
 *
 * There is also no anchor. The ₪288 sits alone with nothing to be measured
 * against, while the main price at the top of the same page does exactly the
 * right thing: ₪119 struck through above ₪99. The pills should not contradict
 * the pattern the page has already established.
 *
 * And with three options and no marker, nothing tells anyone which to take.
 * The best per-unit price is on the LAST pill, the least prominent position.
 *
 * ── WHY THIS IS A SEPARATE FILE ───────────────────────────────────────────
 * The price, per-unit figure and percentage are already injected at runtime by
 * the theme's own enhancement — the server HTML for a pill contains only the
 * variant title, "יחידה אחת". That code works and is not worth the risk of
 * rewriting. This layer runs after it, reads what is on screen, and adds the
 * three missing pieces. If it fails or the pills change shape, the existing
 * pills are exactly as they are today.
 *
 * ── HOW QUANTITY IS DETERMINED ────────────────────────────────────────────
 * From the variant title, which on this store carries it in words: "יחידה
 * אחת", "6 יחידות", "12 יחידות". A title with no readable count is skipped
 * rather than guessed at — this is the same catalogue where a whitelist keyed
 * on one spelling of an option name missed the second most common one, and a
 * wrong saving printed next to a price is worse than no saving at all.
 *
 * The single-unit price comes from the variant whose count is 1. If there is
 * no such variant there is nothing to compare against, and the script does
 * nothing at all.
 */
(function () {
  'use strict';

  /* NO 'already done' FLAG, and that is the whole lesson from the first
   * version. It set one per pill and returned early on later passes — but the
   * theme's own enhancement REBUILDS the pill's inner spans when a variant is
   * selected, which wiped the anchor and the shekel figure (children of those
   * spans) while the badge survived as a direct child of the pill. The flag
   * then stopped the observer from putting them back. Every insertion below
   * checks for its own element instead, so re-running is free and correct. */

  function count(title) {
    var t = String(title || '');
    if (/יחידה\s*אחת/.test(t)) return 1;
    var m = t.match(/(\d+)\s*יחיד/);
    if (m) return parseInt(m[1], 10);
    m = t.match(/^\s*(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : null;
  }

  /* Number, space, symbol — the format the rest of this page already uses
   * ("360.00 ₪"). The first version wrote "₪714", symbol first, and inside an
   * RTL container that put two opposing bidi runs side by side: the reordering
   * pushed the digits of both prices together and the 6px margin landed where
   * it separated nothing. The margin was applied the whole time — computed
   * margin-left was 6px — it just was not between the two numbers a reader
   * sees. Matching the page's format removes the conflict at the source. */
  function money(n) {
    return Math.round(n).toLocaleString('en-US') + ' ₪';
  }

  function norm(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }

  function enhance(product) {
    var byLabel = {};
    var single = null;
    product.variants.forEach(function (v) {
      var c = count(v.title);
      if (c === null) return;
      byLabel[norm(v.title)] = { qty: c, price: v.price / 100 };
      if (c === 1) single = v.price / 100;
    });
    if (single === null) return;          /* nothing to anchor against */

    var pills = document.querySelectorAll('.t4s-swatch__item');
    if (!pills.length) return;

    /* Best value first, so the badge can be placed in one pass. */
    var best = null;
    pills.forEach(function (pill) {
      var v = byLabel[norm(pill.getAttribute('data-value') || pill.textContent.split('\n')[0])];
      if (!v || v.qty < 2) return;
      var per = v.price / v.qty;
      if (!best || per < best.per) best = { pill: pill, per: per };
    });

    pills.forEach(function (pill) {
      var label = norm(pill.getAttribute('data-value') || pill.textContent.split('\n')[0]);
      var v = byLabel[label];
      if (!v || v.qty < 2) return;

      var full = single * v.qty;
      var saved = full - v.price;
      if (saved <= 0) return;

      /* 1. the anchor, matching what the main price already does */
      var priceEl = pill.querySelector('.nadi-swatch-price');
      if (priceEl && !priceEl.querySelector('.nadi-deal__was')) {
        var was = document.createElement('s');
        was.className = 'nadi-deal__was';
        was.textContent = money(full);
        priceEl.insertBefore(was, priceEl.firstChild);
      }

      /* 2. the saving in shekels, appended to the percentage already there */
      var saveEl = pill.querySelector('.nadi-qty__save');
      if (saveEl && !saveEl.querySelector('.nadi-deal__ils')) {
        var ils = document.createElement('span');
        ils.className = 'nadi-deal__ils';
        ils.textContent = ' · ' + money(saved);
        saveEl.appendChild(ils);
      }

      /* 3. the marker, on the best per-unit price only */
      if (best && best.pill === pill && !pill.querySelector('.nadi-deal__badge')) {
        var b = document.createElement('span');
        b.className = 'nadi-deal__badge';
        b.textContent = 'הכי משתלם';
        pill.appendChild(b);
      }

    });
  }

  function run() {
    if (!/\/products\//.test(location.pathname)) return;
    var handle = (location.pathname.split('/products/')[1] || '').split(/[?#]/)[0];
    if (!handle) return;

    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (!product || !product.variants || product.variants.length < 2) return;
        enhance(product);
        /* The pills are built by another script and can be rebuilt when a
           variant is chosen, so watch rather than assume one pass is enough. */
        var host = document.querySelector('.t4s-swatch__list') || document.body;
        new MutationObserver(function () { enhance(product); })
          .observe(host, { childList: true, subtree: true });
      })
      .catch(function () { /* the pills stay exactly as they are today */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
